/**
 * "Invite to Teslyn" on a Staff row (tenant admins; decision 660): the address (asked for, and saved to the register,
 * when the person has none), the sign-in roles ticked from their register roles, and what happens on acceptance.
 */
import { useState, type ReactElement } from 'react';
import {
  INVITE_STAFF_ROLES,
  REGISTER_ROLE_LABEL,
  REGISTER_ROLE_SIGN_IN,
  defaultInviteRoles,
  normaliseRoles,
  type InviteStaffRole,
  type Person,
} from '@teslyn/domain';
import { ROLE_LABEL } from '../access/roles.ts';
import { Button } from '../components/Button.tsx';
import { CheckField, TextField } from '../components/Fields.tsx';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteToTeslynPanel({
  person,
  onSend,
  onCancel,
}: {
  readonly person: Person;
  readonly onSend: (email: string, roles: readonly InviteStaffRole[]) => Promise<void>;
  readonly onCancel: () => void;
}): ReactElement {
  const registerEmail = person.email?.trim() ?? '';
  const [email, setEmail] = useState(registerEmail);
  const [roles, setRoles] = useState<readonly InviteStaffRole[]>(defaultInviteRoles(person));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const from = normaliseRoles(person.roles ?? [person.role])
    .map((r) => `${REGISTER_ROLE_LABEL[r]}: ${ROLE_LABEL[REGISTER_ROLE_SIGN_IN[r]]}`)
    .join('; ');

  async function send(): Promise<void> {
    const addr = email.trim().toLowerCase();
    if (!EMAIL.test(addr)) {
      setError('Enter a valid email address, for example kyle@example.com.');
      return;
    }
    if (roles.length === 0) {
      setError('Choose at least one role.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSend(
        addr,
        INVITE_STAFF_ROLES.filter((r) => roles.includes(r)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="panel stack"
      aria-label={`Invite ${person.name} to Teslyn`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <h2 className="heading">Invite {person.name} to Teslyn</h2>
      <p className="body">
        Teslyn emails an invitation. As soon as {person.name} accepts it and signs in, they are linked to that
        sign-in here, so Teslyn writes their name (not their email) on refly ticks and the like.
      </p>
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
      {registerEmail ? (
        <p className="body">
          Email: <strong>{registerEmail}</strong>{' '}
          <span className="muted">(from the register; to change it, use Edit first)</span>
        </p>
      ) : (
        <TextField
          label="Email"
          type="email"
          hint={`${person.name} has no email in the register yet: this one is saved there.`}
          value={email}
          onChange={setEmail}
        />
      )}
      <fieldset className="fieldset stack">
        <legend className="label muted">Teslyn roles</legend>
        {INVITE_STAFF_ROLES.map((r) => (
          <CheckField
            key={r}
            label={ROLE_LABEL[r]}
            checked={roles.includes(r)}
            onChange={(on) => setRoles((now) => (on ? [...now, r] : now.filter((x) => x !== r)))}
          />
        ))}
        <p className="caption muted">
          Ticked from the register roles ({from}). Tenant admin is never ticked for you. They can do
          everything each role allows; change roles later in Users &amp; roles.
        </p>
      </fieldset>
      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          Send invitation
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
